import { X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '../../Shared/Input/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../Shared/Sheet/sheet';
import { SheetConfirmationCloseDialog } from '../SheetConfirmationCloseDialog';
import { ConnectRemoteMcpForm } from './ConnectRemoteMcpAction.form';
import { RemoteMcpActionTools } from './RemoteMcpActionTools';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { useRemoteMcpActionForm } from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpActionForm.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

export const RemoteMcpActionSheet = () => {
  const { t } = useTranslation('remote_mcp');
  const { isOpen, closeModal, params } = useModalStore();

  const { remoteMcpAction } = params || {};
  const { form, handleSubmit } = useRemoteMcpActionForm(remoteMcpAction);

  const scrollContainerRef = useRef<HTMLElement>(null);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const handleConfirmClose = () => {
    setConfirmationModalOpen(false);
    closeModal();
    form.reset();
  };

  const { isDirty } = useFormState({ control: form.control });
  const handleClose = () => {
    if (isDirty) {
      setConfirmationModalOpen(true);
    } else {
      closeModal();
      form.reset();
    }
  };

  return (
    <Form {...form}>
      <Sheet open={isOpen(MODAL_NAMES.REMOTE_MCP_DIALOG)} onOpenChange={closeModal}>
        <SheetContent
          aria-describedby={undefined}
          className="max-w-3xl w-full gap-0"
          tabIndex={undefined}
        >
          <SheetHeader className="pb-4">
            <SheetTitle>{t('title')}</SheetTitle>
            <div className="flex items-center gap-2 pr-1">
              <Button
                hidden={!remoteMcpAction}
                onClick={form.handleSubmit(handleSubmit)}
                data-testid="remote-mcp-action-sheet-submit-button"
              >
                {t('button.save')}
              </Button>
              <IconButton variant="secondary" onClick={handleClose}>
                <X className="size-4" />
              </IconButton>
            </div>
          </SheetHeader>
          <div className="grid grid-cols-1 h-full overflow-hidden">
            <section
              ref={scrollContainerRef}
              className="space-y-7 relative px-1 h-full custom-scroll overflow-y-auto scroll-stable"
            >
              <div hidden={!remoteMcpAction} data-testid="remote-mcp-action-form-name-input">
                <FormField
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('form.name.label')}</FormLabel>
                      <FormDescription>{t('form.name.description')}</FormDescription>
                      <FormControl>
                        <Input autoFocus placeholder={t('form.name.placeholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <ConnectRemoteMcpForm
                remoteMcpAction={remoteMcpAction}
                withAccordion={!!remoteMcpAction}
              />
              <RemoteMcpActionTools
                form={form}
                isHidden={
                  !remoteMcpAction ||
                  (!!remoteMcpAction.oauth && remoteMcpAction.oauth.status !== 'authorized')
                }
                name="allowed_tools"
              />
            </section>
          </div>
          <SheetConfirmationCloseDialog
            confirmationModalOpen={confirmationModalOpen}
            setConfirmationModalOpen={setConfirmationModalOpen}
            handleConfirmClose={handleConfirmClose}
          />
        </SheetContent>
      </Sheet>
    </Form>
  );
};
